// Stores
import { useRoomStore } from '@/stores/room'
import { useUiStoreActions } from '@/stores/ui'
import { FEATURES } from '@/stores/features'
import { CopyRoomLink } from './CopyRoomLink'
import { TEST_IDS } from '@/helpers/constants'

// Components
import { ScrollArea } from '@/components/base/scroll-area'
import { DialogDevices } from '@/components/DialogDevices'
import { Feature } from '@/components/Feature'

// Types
import { useSelfRoomLockButtonState } from '@/hooks/useSelfRoomLockButtonState'
import { ButtonPanelOption } from '@/components/ButtonPanelOption'
import { ButtonRoomLock } from '@/components/ButtonRoomLock'
import { useSelfLayoutButtonState } from '@/hooks/useSelfLayoutButtonState'
import { ButtonRoomLayout } from '@/components/ButtonRoomLayout'
import type { RoomPanelType } from '@/helpers/panelMap'

export const PanelRoomOptions = () => {
  const isAudioOnly = useRoomStore(state => state.isAudioOnly)
  const callState = useRoomStore(state => state.callState)
  const memberId = useRoomStore(state => state.memberId)
  const path = useRoomStore(state => state.path)

  const { isRoomLockButtonEnabled, isRoomLockButtonVisible } =
    useSelfRoomLockButtonState()
  const { isRoomLayoutButtonVisible } = useSelfLayoutButtonState()

  const { setRoomPanelType, setAdvancedPanel } = useUiStoreActions()

  const changePanelContentHandler = (panelType: RoomPanelType) => {
    setRoomPanelType(panelType)
  }

  const callJoined = callState === 'joined'

  return (
    <ScrollArea className="h-full" data-testid={TEST_IDS.ROOM_PANEL_SETTINGS}>
      <div className="flex w-full flex-col space-y-3">
        <ButtonRoomLock
          disabled={!callJoined && !isRoomLockButtonEnabled}
          isVisible={isRoomLockButtonVisible}
        />
        <Feature name={FEATURES.CREATE_SIDEBAR}>
          <ButtonPanelOption
            disabled
            isVisible={!isAudioOnly}
            tag="view-sidebar"
          >
            Create Sidebar
          </ButtonPanelOption>
        </Feature>
        {!isAudioOnly && (
          <CopyRoomLink
            address={path}
            clickedContent="Copied!"
            initialContent="Copy Room Link"
            withArrow={false}
          >
            {({ trigger }) => (
              <ButtonPanelOption tag="share" onClick={trigger}>
                Share Room
              </ButtonPanelOption>
            )}
          </CopyRoomLink>
        )}
        <Feature name={FEATURES.CHANGE_ROOMS}>
          <ButtonPanelOption
            disabled={!callJoined}
            onClick={() => changePanelContentHandler('navigator')}
            isVisible={!isAudioOnly}
            tag="sofa-outline-1"
          >
            Change Rooms
          </ButtonPanelOption>
        </Feature>
        <DialogDevices showCameraPreview={!isAudioOnly}>
          <ButtonPanelOption tag="headphones">Change Devices</ButtonPanelOption>
        </DialogDevices>
        <ButtonRoomLayout
          disabled={!callJoined}
          onClick={() => changePanelContentHandler('layout')}
          isVisible={isRoomLayoutButtonVisible}
        />
        <Feature name={FEATURES.ROOM_ADVANCED}>
          <ButtonPanelOption
            disabled={!callJoined}
            onClick={() => setAdvancedPanel(memberId)}
            isVisible={true}
            tag="settings"
          >
            Advanced
            <span className="sr-only">Settings</span>
          </ButtonPanelOption>
        </Feature>
      </div>
    </ScrollArea>
  )
}
